import React, {useEffect, useRef, useState} from 'react';

import {ICombinedData, ITableConfigProp} from '../common/api/interfaces/interfaces';
import {getDataConfigLocalFullyFilledConfig, getDateFromGivenConfig, remakeArrayIntoOtherInterface} from '../utils';

import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Dialog} from 'primereact/dialog';
import {Button} from 'primereact/button';
import {ProgressSpinner} from 'primereact/progressspinner';

import {DatasetDataLagLeadRequest, UpdateDatasetData} from "api/requests";

import {
    BooleanTypeTemplate,
    NumberTypeTemplate,
    TitleOverrideAndDescriptionTemplate
} from "../../../templates/DataTableCellTemplates";

import {useGetDatasetDataInfo} from "../../../../api/api-hooks/apiHooks";
import DataInputOutputUpdateTable from "../update-dataset-table/DataInputOutputUpdateTable";

import './DataInputOutputTable.scss';
import {ChronoUnit} from "../../../../utils/time-utils";
import {Toast, ToastSeverityType} from "primereact/toast";
import {DatasetDataInfo} from "../../../../api/DatasetDataApi";
import {Message} from "primereact/message";

const DataInputOutputTable = (props: ITableConfigProp) => {
    const identifiers = props.datasets.map((ds) => {
        return ds.identifier as string
    })

    const config: DatasetDataLagLeadRequest = getDataConfigLocalFullyFilledConfig(props.config || {});
    const [dataQuery, setDataQuery] = useState<Array<ICombinedData>>([])

    const [dialog, setDialog] = useState(false);
    const dialogFuncMap: any = {'dialog': setDialog}

    const myToast = useRef<Toast>(null)

    const {data, isError, isLoading, refetch} = useGetDatasetDataInfo(identifiers, config, {
        enabled: config && identifiers && identifiers.length > 0,
        refetchInterval: props.config?.updateFrequency && !dialog ? props.config?.updateFrequency * 1000 : undefined,
    })

    useEffect(() => {
        if (data && !isError && !isLoading && data?.length > 0) {
            setDataQuery([...remakeArrayIntoOtherInterface(data, props.datasets)])
        }
    }, [data])

    const showToast = (severityValue: ToastSeverityType, summaryValue: string, detailValue: string) => {
        myToast.current!.show({severity: severityValue, summary: summaryValue, detail: detailValue});
    }

    const onClick = (name: string) => {
        dialogFuncMap[`${name}`](true);
    }

    const onHide = (name: string) => {
        refetch();
        dialogFuncMap[`${name}`](false);
    }

    const titleColumnRender = (rowData: ICombinedData) => {
        return TitleOverrideAndDescriptionTemplate({
            title: rowData.title,
            overriddenTitle: rowData.overriddenTitle,
            description: rowData.description
        });
    }

    const valueColumnRender = (rowData: ICombinedData) => {
        if (rowData.type.toUpperCase() === "NUMBER") {
            return NumberTypeTemplate(rowData.data, rowData.unit, rowData.decimalPlaces);
        } else if (rowData.type.toUpperCase() === "BOOLEAN") {
            const changeBooleanValue = (val: boolean) => {
                const boolToNumber = val ? 1 : 0

                DatasetDataInfo.updateData([{
                    identifier: rowData.identifier,
                    values: {
                        [`${rowData.data[0].key}`]: boolToNumber
                    }
                }] as Array<UpdateDatasetData>)
                    .then(() => {
                        showToast("success", "", "Datasets updated")
                        setDataQuery(oldDataQuery => {
                            return oldDataQuery.map(ob => {
                                if (ob.identifier === rowData.identifier) {
                                    ob.data[0].value = boolToNumber
                                }
                                return ob
                            })
                        })
                    })
                    .catch(er => {
                        console.error(er)
                        showToast("error", "", "Error while updating datasets")
                    })
            }

            return BooleanTypeTemplate(rowData.data, changeBooleanValue)
        } else {
            return "Unknown type"
        }

    }

    return <>
        <Toast ref={myToast} position={"bottom-right"}/>
        {isError &&
            <div className={"error-window"}>
                <Message severity="error" text="Error while fetching datasets"></Message>
            </div>
        }

        {isLoading && !isError &&
            <ProgressSpinner style={{display: "flex", justifyContent: "space-around", overflow: "hidden"}}/>
        }

        {data && !isError &&
            <div className={"full-table"}>
                <DataTable width={"100%"} value={dataQuery} responsiveLayout="scroll" scrollHeight={"450px"}
                           size="small">
                    <Column field={"title"}
                            header="Dataset"
                            body={titleColumnRender}
                            style={{paddingLeft: "15px"}}/>
                    <Column field={"unit"}
                            header="Value"
                            body={valueColumnRender}
                            style={{
                                width: '10px',
                                paddingRight: "15px",
                                whiteSpace: "nowrap"
                            }}/>
                </DataTable>

                {data && data.length > 0 &&
                    <Button style={{margin: "0.3vw"}} label="Update"
                            className="p-button-success p-button-rounded p-button-sm"
                            icon="pi pi-pencil" onClick={() => onClick('dialog')}/>
                }

                <Dialog breakpoints={{'960px': '75vw', '640px': '100vw'}}
                        style={{width: '50vw'}}
                        draggable={false}
                        resizable={false}
                        header="Update Datasets"
                        visible={dialog}
                        onHide={() => onHide('dialog')}
                        blockScroll
                >
                    <DataInputOutputUpdateTable titleColumnReader={titleColumnRender}
                                                identifiers={identifiers}
                                                firstLoadDate={getDateFromGivenConfig(props.config || {})}
                                                datasets={props.datasets}
                                                dateFrequency={props.config?.dataFrequency as ChronoUnit || ChronoUnit.MINUTES}
                                                closeDialog={() => onHide('dialog')}
                                                showToast={showToast}
                    />
                </Dialog>
            </div>
        }
    </>;
}

export default DataInputOutputTable;